import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
// import { DataFrame, GeoDataFrame, Dataset } from '@oceanum/datamesh-js-core';
import { Dataset } from '@oceanum/datamesh';
import { bboxClip, bboxPolygon, pointsWithinPolygon } from '@turf/turf';

import { useDataLoading } from 'framework';

dayjs.extend(duration);

const dataLoading = useDataLoading();

const array_indexer = (array, indexer, indexdim, idim = 0) => {
  if (idim == indexdim) {
    const a = [];
    indexer.map((i) => {
      a.push(array[i]);
    });
    return a;
  } else {
    return array.map((v) => array_indexer(v, indexer, indexdim, idim + 1));
  }
};

// Build geo selectors for bbox filtering on x/y dimensions
// Supports both 1D coordinate arrays and 2D lon/lat grids
// Optional subset parameter subsamples data by taking every Nth point
const build_geo_selectors = async (data, geofilter, xname, yname) => {
  if (!geofilter || geofilter.type !== 'bbox' || !xname || !yname) {
    return null;
  }

  const [x0, y0, x1, y1] = geofilter.geom;
  const maxGridSize = geofilter.maxGridSize || null;
  const xcoord = await data.variables[xname].get();
  const ycoord = await data.variables[yname].get();
  const xdims = data.variables[xname].dimensions;
  const ydims = data.variables[yname].dimensions;

  // Check if coordinates are 2D (same dimensions for x and y)
  const is2D =
    xdims.length === 2 &&
    ydims.length === 2 &&
    xdims[0] === ydims[0] &&
    xdims[1] === ydims[1];

  if (is2D) {
    // 2D coordinate arrays - find minimum bounding box indices
    const ny = xcoord.length;
    const nx = xcoord[0].length;
    let iMin = nx,
      iMax = -1,
      jMin = ny,
      jMax = -1;

    for (let j = 0; j < ny; j++) {
      for (let i = 0; i < nx; i++) {
        const x = xcoord[j][i];
        const y = ycoord[j][i];
        if (x >= x0 && x <= x1 && y >= y0 && y <= y1) {
          if (i < iMin) iMin = i;
          if (i > iMax) iMax = i;
          if (j < jMin) jMin = j;
          if (j > jMax) jMax = j;
        }
      }
    }

    if (iMax < 0 || jMax < 0) {
      return null;
    }

    // Dimensions are [dim0, dim1] corresponding to [j, i]
    const dim0 = xdims[0];
    const dim1 = xdims[1];

    // Calculate subset step based on bbox-filtered size and maxGridSize
    const bboxNx = iMax - iMin + 1;
    const bboxNy = jMax - jMin + 1;
    const subset = maxGridSize
      ? Math.max(1, Math.ceil(Math.max(bboxNx, bboxNy) / maxGridSize))
      : 1;

    // Apply subset to create subsampled indices
    const jIndices = [];
    for (let j = jMin; j <= jMax; j += subset) jIndices.push(j);
    const iIndices = [];
    for (let i = iMin; i <= iMax; i += subset) iIndices.push(i);

    return {
      selectors: {
        [dim0]: { start: jMin, stop: jMax + 1 },
        [dim1]: { start: iMin, stop: iMax + 1 },
      },
      dimensions: {
        [dim0]: jIndices.length,
        [dim1]: iIndices.length,
      },
      indices:
        subset > 1
          ? {
              [dim0]: jIndices.map((j) => j - jMin),
              [dim1]: iIndices.map((i) => i - iMin),
            }
          : null,
    };
  } else {
    // 1D coordinate arrays
    const xIndices = [];
    for (let i = 0; i < xcoord.length; i++) {
      if (xcoord[i] >= x0 && xcoord[i] <= x1) xIndices.push(i);
    }
    const yIndices = [];
    for (let i = 0; i < ycoord.length; i++) {
      if (ycoord[i] >= y0 && ycoord[i] <= y1) yIndices.push(i);
    }

    if (xIndices.length === 0 || yIndices.length === 0) {
      return null;
    }

    // Calculate subset step based on bbox-filtered size and maxGridSize
    const subset = maxGridSize
      ? Math.max(
          1,
          Math.ceil(Math.max(xIndices.length, yIndices.length) / maxGridSize),
        )
      : 1;

    // Apply subset to subsample indices
    const xSubset = [];
    for (let i = 0; i < xIndices.length; i += subset) xSubset.push(xIndices[i]);
    const ySubset = [];
    for (let i = 0; i < yIndices.length; i += subset) ySubset.push(yIndices[i]);

    return {
      selectors: {
        [xname]: {
          start: xIndices[0],
          stop: xIndices[xIndices.length - 1] + 1,
          step: subset,
        },
        [yname]: {
          start: yIndices[0],
          stop: yIndices[yIndices.length - 1] + 1,
          step: subset,
        },
      },
      dimensions: {
        [xname]: xSubset.length,
        [yname]: ySubset.length,
      },
      indices: null,
    };
  }
};

// Apply geo selectors to a variable and return filtered data
const apply_geo_filter = async (variable, geoResult) => {
  if (!geoResult) return variable.get();

  const dims = variable.dimensions;
  if (!dims) return variable.get();

  const selector = dims.map((d) => geoResult.selectors[d] || null);
  const hasSelector = selector.some((s) => s !== null);

  if (!hasSelector) return variable.get();

  let data = await variable.get(selector);

  // Apply non-contiguous indexing if needed (1D case)
  if (geoResult.indices) {
    for (const [dimName, indices] of Object.entries(geoResult.indices)) {
      const dind = dims.indexOf(dimName);
      if (dind >= 0) {
        data = array_indexer(data, indices, dind, 0);
      }
    }
  }

  return data;
};

//Squeeze singleton dimensions
export const squeeze_dataset = (data) => {
  Object.keys(data.variables).forEach((k) => {
    let dimensions = [...data.variables[k].dimensions];
    if (dimensions.length > 1) {
      data.variables[k].dimensions.forEach((d) => {
        if (data.dimensions[d] == 1) {
          data.variables[k].data = data.variables[k].data[0];
          dimensions = dimensions.filter((dim) => dim != d);
        }
      });
      data.variables[k].dimensions = dimensions;
    }
  });
  return data;
};

export class DataWrapper {
  constructor(id, datasource, oceanql = {}, refresh = false) {
    this.id = id;
    this.datasource = datasource;
    this.oceanql = oceanql;
    this.refresh = refresh;
  }
  static async inline(id, data) {
    let datasource = null;
    if ((data.features && data.features.length) || data.geometry) {
      datasource = await Dataset.fromGeojson(data);
    } else if (data.variables) {
      const { coordkeys, ..._datasource } = data;
      datasource = await Dataset.init(_datasource, coordkeys);
    }
    return new DataWrapper(id, datasource, {}, true);
  }
  async query(query, squeeze) {
    if (!this.datasource) return null;
    const tname = this.datasource.coordkeys.t;
    const zname = this.datasource.coordkeys.z;
    const xname = this.datasource.coordkeys.x;
    const yname = this.datasource.coordkeys.y;
    const gname = this.datasource.coordkeys.g;
    const qt = tname && query?.timefilter;
    const ql = zname && query?.levelfilter;
    const qg = query?.geofilter;

    dataLoading.start(this.id);
    const data = this.datasource;
    const new_variables = {};
    const dimensions = { ...data.dimensions };
    const variables = query?.variables || Object.keys(data.variables);

    // Build geo selectors for bbox filtering
    const geoResult = await build_geo_selectors(data, qg, xname, yname);
    if (qg && qg.type === 'bbox' && !geoResult) {
      dataLoading.stop(this.id);
      return null;
    }
    if (geoResult) {
      Object.assign(dimensions, geoResult.dimensions);
    }

    // Handle level filter (z coordinate) - build selector like geo
    let levelResult = null;
    if (ql) {
      const zcoord = await data.variables[zname].get();
      let ind = Array.from(zcoord.keys());
      if (ql.type == 'nearest') {
        const z0 = ql.levels[0];
        const zdiff = new Float64Array(zcoord.length);
        zcoord.forEach((z, i) => (zdiff[i] = Math.abs(z - z0)));
        ind.sort((a, b) => (zdiff[a] < zdiff[b] ? -1 : 1));
        ind = ind.slice(0, 1);
        // Apply tolerance if specified
        if (ql.tolerance != null) {
          ind = ind.filter((i) => Math.abs(zcoord[i] - z0) <= ql.tolerance);
        }
      } else if (ql.type == 'range') {
        const z0 = ql.levels[0];
        const z1 = ql.levels[1];
        const indexes = zcoord.map((_, i) => i);
        ind = indexes.filter((i) => z0 <= zcoord[i] && zcoord[i] <= z1);
      }
      if (ind.length == 0) {
        dataLoading.stop(this.id);
        return null;
      }
      ind.sort((a, b) => a - b);
      const levelDim = data.variables[zname].dimensions[0];
      const l0 = ind[0];
      const l1 = ind[ind.length - 1] + 1;
      // Check if indices are contiguous
      const isContiguous = ind.length === l1 - l0;
      levelResult = {
        dim: levelDim,
        selector: { [levelDim]: { start: l0, stop: l1 } },
        indices: isContiguous ? null : ind.map((i) => i - l0),
      };
      dimensions[levelDim] = ind.length;
    }

    if (qt) {
      const tcoord = await data.variables[tname].get();
      let ind = Array.from(tcoord.keys());
      if (qt.type == 'nearest') {
        const t0 = dayjs(qt.times[0]).unix();
        const tdiff = new Float64Array(tcoord.length);
        tcoord.forEach((t, i) => (tdiff[i] = Math.abs(t - t0)));
        ind.sort((a, b) => (tdiff[a] < tdiff[b] ? -1 : 1));
        if (qt.groupby) {
          const group = await data.variables[qt.groupby].get();
          const groupind = ind.reduce((g, i) => {
            let v = group[i];
            if (!g[v]) g[v] = i;
            return g;
          }, {});
          ind = Object.values(groupind);
        } else {
          ind = ind.slice(0, 1);
        }
        const tol =
          (qt.tolerance && dayjs.duration(qt.tolerance).as('seconds')) ||
          tcoord[1] - tcoord[0];
        ind = ind.filter((i) => Math.abs(tcoord[i] - t0) <= tol);
      } else if (qt.type == 'range') {
        const t0 = dayjs(qt.times[0]).unix();
        const t1 = dayjs(qt.times[1]).unix();
        const indexes = tcoord.map((_, i) => i);
        ind = indexes.filter((i) => t0 <= tcoord[i] && tcoord[i] <= t1);
      }
      if (ind.length == 0) {
        return null;
      }
      //Sort by index
      ind.sort((a, b) => a - b);
      const i0 = ind[0];
      const i1 = ind[ind.length - 1] + 1;
      //Sort by time
      ind.sort((a, b) => tcoord[a] - tcoord[b]);
      for (const k of variables) {
        if (!data.variables[k]) {
          console.warn('Variable ' + k + ' not found');
          continue;
        }
        new_variables[k] = {
          dimensions: data.variables[k].dimensions,
          attributes: data.variables[k].attributes,
        };
        const tdim = data.variables[tname].dimensions[0];
        if (data.variables[k].dimensions) {
          const tdind = data.variables[k].dimensions.indexOf(tdim);
          if (tdind < 0) {
            // Build selector with geo and level
            const dims = data.variables[k].dimensions;
            const selector = dims.map(
              (d) =>
                geoResult?.selectors[d] || levelResult?.selector[d] || null,
            );
            let _var = await data.variables[k].get(selector);
            // Apply non-contiguous geo indexing if needed
            if (geoResult?.indices) {
              for (const [dimName, indices] of Object.entries(
                geoResult.indices,
              )) {
                const dind = dims.indexOf(dimName);
                if (dind >= 0) _var = array_indexer(_var, indices, dind, 0);
              }
            }
            // Apply non-contiguous level indexing if needed
            if (levelResult?.indices) {
              const ldind = dims.indexOf(levelResult.dim);
              if (ldind >= 0) {
                _var = array_indexer(_var, levelResult.indices, ldind, 0);
              }
            }
            new_variables[k].data = _var;
          } else {
            // Build combined selector with time, geo, and level
            const dims = data.variables[k].dimensions;
            const selector = dims.map(
              (d) =>
                geoResult?.selectors[d] || levelResult?.selector[d] || null,
            );
            selector[tdind] = { start: i0, stop: i1 };
            let _var = await data.variables[k].get(selector);
            const _ind = ind.map((i) => i - i0);
            _var = array_indexer(_var, _ind, tdind, 0);
            // Apply non-contiguous geo indexing if needed
            if (geoResult?.indices) {
              for (const [dimName, indices] of Object.entries(
                geoResult.indices,
              )) {
                const dind = dims.indexOf(dimName);
                if (dind >= 0) _var = array_indexer(_var, indices, dind, 0);
              }
            }
            // Apply non-contiguous level indexing if needed
            if (levelResult?.indices) {
              const ldind = dims.indexOf(levelResult.dim);
              if (ldind >= 0) {
                _var = array_indexer(_var, levelResult.indices, ldind, 0);
              }
            }
            new_variables[k].data = _var;
          }
        }
      }
      dimensions[tname] = ind.length;
    } else {
      for (const k of variables) {
        if (!data.variables[k]) {
          console.warn('Variable ' + k + ' not found');
          continue;
        }
        // Build selector with geo and level
        const dims = data.variables[k].dimensions;
        const selector = dims?.map(
          (d) => geoResult?.selectors[d] || levelResult?.selector[d] || null,
        );
        const hasSelector = selector?.some((s) => s !== null);
        let _var = hasSelector
          ? await data.variables[k].get(selector)
          : await data.variables[k].get();
        // Apply non-contiguous geo indexing if needed
        if (geoResult?.indices && dims) {
          for (const [dimName, indices] of Object.entries(geoResult.indices)) {
            const dind = dims.indexOf(dimName);
            if (dind >= 0) _var = array_indexer(_var, indices, dind, 0);
          }
        }
        // Apply non-contiguous level indexing if needed
        if (levelResult?.indices && dims) {
          const ldind = dims.indexOf(levelResult.dim);
          if (ldind >= 0) {
            _var = array_indexer(_var, levelResult.indices, ldind, 0);
          }
        }
        new_variables[k] = {
          dimensions: data.variables[k].dimensions,
          attributes: data.variables[k].attributes,
          data: _var,
        };
      }
    }
    this.refresh = false;
    dataLoading.stop(this.id);
    if (squeeze) {
      return squeeze_dataset({
        attributes: data.attributes,
        variables: new_variables,
        dimensions,
        coordkeys: this.datasource.coordkeys,
      });
    } else {
      return {
        attributes: data.attributes,
        variables: new_variables,
        dimensions,
        coordkeys: this.datasource.coordkeys,
      };
    }
  }
}
